import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, FormControl, InputLabel, Select,
  MenuItem, Checkbox, ListItemText, Chip, Paper, Avatar, Skeleton, Fade, Alert,
  Button, Collapse, Divider, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Snackbar, IconButton, Pagination, Tabs, Tab,
  Tooltip, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TagIcon from '@mui/icons-material/Tag';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Navbar from '../components/Navbar.js';
import NotificationsCard from '../components/NotificationsCard.js';
import GroupedBars from '../components/GroupedBars.js';
import InsightsBreakdown from '../components/InsightsBreakdown.js';
import StackedBars from '../components/StackedBars.js';
import { typeGroups } from '../components/messageTypes.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import { downloadUsageCsv } from './OrgBillingPage.js';

// Funnel filter config — drives the dropdown and the is_* query param.
const FILTERS = [
  { key: 'needs_demo', label: 'Pre-Demo (needs demo)', param: 'is_pre_demo' },
  { key: 'post_demo', label: 'Post-Demo', param: 'is_post_demo' },
  { key: 'free_trial', label: 'Free trial', param: 'is_free_trial' },
  { key: 'active', label: 'Active', param: 'is_active' },
  { key: 'deactivated', label: 'Deactivated', param: 'is_deactivated' },
];
const DEFAULT_FILTER = 'needs_demo';
const PAGE_SIZE = 20;
const METRIC_LABEL = { appointments: 'Appointments', sms: 'SMS', email: 'Email', invites: 'Invites' };

const statusConfig = {
  needs_demo: { label: 'Needs demo', bg: '#fff8e1', text: '#8a6d00', borderColor: '#ffca28' },
  post_demo: { label: 'Post-demo', bg: '#e3f2fd', text: '#1565c0', borderColor: '#42a5f5' },
  free_trial: { label: 'Free trial', bg: '#e0f7f9', text: '#00838f', borderColor: '#26c6da' },
  active: { label: 'Active', bg: '#e8f5e9', text: '#2e7d32', borderColor: '#4caf50' },
  deactivated: { label: 'Deactivated', bg: '#fce4ec', text: '#c62828', borderColor: '#ef9a9a' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: '2-digit', month: 'numeric', day: 'numeric' }); }
  catch { return s; }
};

// Filterable insights: appointments + messages by organization / category / date window.
function InsightsPanel() {
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState('');       // '' = all orgs
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState('');       // '' = all categories
  const [days, setDays] = useState(0);      // 0 = all time
  const [bucket, setBucket] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllRows, setShowAllRows] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Grouped-by-category chart (sqip admin only — an org admin scopes with the category filter).
  const [groupByCat, setGroupByCat] = useState(false);
  const [metric, setMetric] = useState('appointments');

  useEffect(() => {
    apiCall(ENDPOINTS.ORGANIZATIONS(200))
      .then((d) => setOrgs(((d && d.results) || d || []).map((o) => ({ id: o.id, name: o.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!org) return;   // cat/cats are reset by the organization picker's onChange
    apiCall(ENDPOINTS.CATEGORIES_BY_ORG(org))
      .then((d) => setCats(((d && d.results) || d || []).map((c) => ({ id: c.id, name: c.name || `Category #${c.id}` }))))
      .catch(() => setCats([]));
  }, [org]);

  const buildParts = () => {
    const parts = [];
    if (startDate || endDate) {
      if (startDate) parts.push(`start=${startDate}`);
      if (endDate) parts.push(`end=${endDate}`);
    } else if (days) {
      parts.push(`days=${days}`);
    }
    if (org) parts.push(`organization=${org}`);
    if (org && cat) parts.push(`category=${cat}`);
    parts.push(`bucket=${bucket}`);
    return parts;
  };

  const load = useCallback(async () => {
    setLoading(true);
    const parts = buildParts();
    const url = ENDPOINTS.ONBOARDING_INSIGHTS + (parts.length ? `?${parts.join('&')}` : '');
    try { setData(await apiCall(url)); } catch { setData(null); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, cat, days, bucket, startDate, endDate]);

  // CSV covers the full range (full=1 removes the chart bucket cap), not just the charted window.
  const downloadCsv = async () => {
    const parts = buildParts();
    parts.push('full=1');
    const url = ENDPOINTS.ONBOARDING_INSIGHTS + `?${parts.join('&')}`;
    const full = await apiCall(url).catch(() => data);
    const orgName = org ? (orgs.find((o) => String(o.id) === String(org))?.name || `org_${org}`) : 'platform';
    const catName = org && cat ? (cats.find((c) => String(c.id) === String(cat))?.name || '') : '';
    downloadUsageCsv(full || data, catName ? `${orgName}_${catName}` : orgName);
  };

  useEffect(() => { const id = setTimeout(load, 0); return () => clearTimeout(id); }, [load]);

  const scoped = !!org;
  const appts = scoped ? (data?.appointments_made ?? 0) : (data?.totals?.appointments_made ?? 0);
  const msgs = scoped ? ((data?.sms_sent || 0) + (data?.email_sent || 0)) : (data?.totals?.messages_sent ?? 0);
  const rows = scoped
    ? (data?.by_category || []).map((c) => ({ id: c.category_id, name: c.name || `#${c.category_id}`, a: c.appointments, m: c.messages_sent }))
    : (data?.by_organization || []).map((o) => ({ id: o.organization_id, name: o.name || `#${o.organization_id}`, a: o.appointments_made, m: o.messages_sent }));

  const sel = (label, value, onChange, options, disabled = false) => (
    <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ borderRadius: 2 }}>
        {options.map((o) => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
  const cell = (label, value) => (
    <Box sx={{ flex: 1, minWidth: 100, textAlign: 'center', py: 1.5 }}>
      <Typography variant="h5" fontWeight={800}>{value ?? 0}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2 }}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {sel('Organization', org, (v) => { setOrg(v); setCat(''); setCats([]); }, [{ value: '', label: 'All organizations' }, ...orgs.map((o) => ({ value: o.id, label: o.name }))])}
        {sel('Category', cat, setCat, [{ value: '', label: 'All categories' }, ...cats.map((c) => ({ value: c.id, label: c.name }))], !org)}
        {sel('Window', days, setDays, [{ value: 0, label: 'All time' }, { value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }], !!(startDate || endDate))}
        {sel('Group by', bucket, setBucket, [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }])}
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 140 }} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 140 }} />
        {(startDate || endDate) && (
          <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); }}>Clear dates</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled={!data} onClick={downloadCsv}>CSV</Button>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {loading ? <Skeleton height={64} /> : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {cell('Appointments', appts)}
            <Divider orientation="vertical" flexItem />
            {cell('Messages sent', msgs)}
            {scoped ? (
              <>
                <Divider orientation="vertical" flexItem />
                {cell('SMS', data?.sms_sent)}
                <Divider orientation="vertical" flexItem />
                {cell('Email', data?.email_sent)}
              </>
            ) : (
              <>
                <Divider orientation="vertical" flexItem />
                {cell('OTP verifications', data?.otp?.total)}
              </>
            )}
            <Divider orientation="vertical" flexItem />
            {cell('Invites', scoped ? data?.invites_sent : data?.totals?.invites_sent)}
          </Box>
          <InsightsBreakdown data={data} />
          {data?.series_truncated && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0 }}>
              Totals above cover the full range; the charts show only the most recent{' '}
              {(data?.appointments_series || []).length} {data?.bucket || bucket}s. Pick a date range to chart it all.
            </Alert>
          )}
          <StackedBars dates={data?.dates} bucket={data?.bucket || bucket} label={`Appointments by ${data?.bucket || bucket}`}
            groups={[
              { key: 'walk_in', label: 'Walk-in', color: '#1565C0', series: data?.appointments_by_kind?.walk_in || [] },
              { key: 'scheduled', label: 'Scheduled', color: '#00C4CC', series: data?.appointments_by_kind?.scheduled || [] },
            ]} />
          <StackedBars dates={data?.dates} bucket={data?.bucket || bucket} label={`SMS by ${data?.bucket || bucket}`} groups={typeGroups(data?.sms_by_type)} />
          <StackedBars dates={data?.dates} bucket={data?.bucket || bucket} label={`Emails by ${data?.bucket || bucket}`} groups={typeGroups(data?.email_by_type)} />

          {/* Per-category comparison. Only meaningful once an organization is selected. */}
          {scoped && (data?.category_series || []).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Switch size="small" checked={groupByCat} onChange={(e) => setGroupByCat(e.target.checked)} />}
                  label={<Typography variant="caption">Group by category</Typography>}
                />
                {groupByCat && (
                  <Select size="small" value={metric} onChange={(e) => setMetric(e.target.value)} sx={{ height: 32, minWidth: 140 }}>
                    <MenuItem value="appointments">Appointments</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="invites">Invites</MenuItem>
                  </Select>
                )}
              </Box>
              {groupByCat && (
                <GroupedBars
                  rows={data.category_series} dates={data.dates}
                  bucket={data?.bucket || bucket} metric={metric}
                  label={`${METRIC_LABEL[metric]} by category, per ${data?.bucket || bucket}`}
                />
              )}
            </Box>
          )}

          {rows.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {scoped ? 'By category' : `By organization${!scoped && rows.length >= 50 ? ' (top 50 by activity)' : ''}`}
              </Typography>
              {/* Show only 3 by default (+ expander) so the dashboard doesn't grow long */}
              <Box sx={{ mt: 0.5, ...(showAllRows ? { maxHeight: 240, overflowY: 'auto' } : {}) }}>
                <Stack divider={<Divider flexItem />}>
                  {(showAllRows ? rows : rows.slice(0, 3)).map((r) => (
                    <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{r.a} appts · {r.m} msgs</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
              {rows.length > 3 && (
                <Button size="small" onClick={() => setShowAllRows((v) => !v)} sx={{ mt: 0.5 }}>
                  {showAllRows ? 'Show less' : `Show all ${rows.length}`}
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

function BusinessRow({ business, onAction, trialPkgs = [], paidPkgs = [] }) {
  const [open, setOpen] = useState(false);
  // Offer edits are local until saved: `pending` is null while the row matches the server.
  const [pending, setPending] = useState(null);
  const cfg = statusConfig[business.status] || statusConfig.needs_demo;
  const sub = business.subscription;
  const canSendEmail = ['needs_demo', 'post_demo'].includes(business.status);
  const canManageTrial = business.status === 'free_trial';
  const canReactivate = business.status === 'deactivated';

  // The offer is one choice: '' = pay-first (no trial), or a free-trial package slug (length).
  const defaultTrialSlug = trialPkgs[0] ? trialPkgs[0].slug : 'trial';
  const savedTrialSlug = business.trial_offered ? (business.trial_package || defaultTrialSlug) : '';
  const trialSlug = pending ? pending.trialSlug : savedTrialSlug;
  const dirty = trialSlug !== savedTrialSlug;
  // Already checked out: saving a free-trial offer activates them instead of re-registering.
  const checkedOut = !!business.organization_id;

  const offerPayload = (slug) => ({ trial_offered: !!slug, trial_package: slug || undefined });
  const editOffer = (slug) => setPending({ trialSlug: slug });
  const saveOffer = () => {
    onAction('saveOffer', business, offerPayload(trialSlug));
    setPending(null);
  };

  return (
    <Paper elevation={0} sx={{
      borderRadius: 3, border: `1px solid ${cfg.borderColor}`, borderLeft: `4px solid ${cfg.borderColor}`,
      background: cfg.bg, overflow: 'hidden',
    }}>
      <Box onClick={() => setOpen((o) => !o)} sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}>
        <Avatar sx={{ width: 40, height: 40, background: (t) => t.palette.custom?.gradientPrimary, flexShrink: 0 }}>
          <BusinessOutlinedIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.95rem' }}>{business.business_name}</Typography>
            <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.borderColor}`, fontWeight: 600, fontSize: 11, height: 20 }} />
            {sub?.days_left != null && business.status === 'free_trial' && (
              <Chip label={`${sub.days_left}d left`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{fmtDate(business.registered_at)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <TagIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={business.reference}>
                {business.reference}
              </Typography>
            </Box>
          </Box>
        </Box>
        <ExpandMoreIcon sx={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', color: 'text.secondary' }} />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Divider />
        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(255,255,255,0.6)' }}>
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Email: {business.contact_email || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">Contact: {business.contact_first_name} {business.contact_last_name}</Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary">
                Subscription: {sub.plan_label} · {sub.status}{sub.trial_ends_at ? ` · trial ends ${fmtDate(sub.trial_ends_at)}` : ''}
              </Typography>
            )}
          </Stack>
          {/* Paid package (active/trialing businesses). On a trial it starts when the trial ends. */}
          {business.organization_id && sub && (
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id={`pkg-${business.id}`}>Package</InputLabel>
                <Select labelId={`pkg-${business.id}`} label="Package"
                  value={sub.pending_package || sub.package || ''}
                  onChange={(e) => onAction('choosePackage', business, e.target.value)}>
                  <MenuItem value="" disabled>Select a package…</MenuItem>
                  {paidPkgs.map((p) => (
                    <MenuItem key={p.slug} value={p.slug}>{p.name} — ${p.monthly_price}/mo</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {sub.pending_package && sub.pending_package !== sub.package && (
                <Typography variant="caption" color="text.secondary">→ starts at next cycle / trial end</Typography>
              )}
            </Box>
          )}
          {canSendEmail ? (
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id={`offer-${business.id}`} shrink>Offer</InputLabel>
                <Select labelId={`offer-${business.id}`} label="Offer" value={trialSlug} displayEmpty notched
                  onChange={(e) => editOffer(e.target.value)}>
                  <MenuItem value="">No free trial (pay-first)</MenuItem>
                  {trialPkgs.map((p) => <MenuItem key={p.slug} value={p.slug}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
              {dirty && (
                <Button size="small" variant="contained" onClick={saveOffer} sx={{ borderRadius: 6 }}>
                  {checkedOut && trialSlug ? 'Save & activate' : 'Save'}
                </Button>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Offer: {business.trial_offered ? `Free trial${business.trial_package ? ` (${business.trial_package})` : ''}` : 'Pay-first'}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canSendEmail && (
              <Button size="small" variant="contained" startIcon={<SendOutlinedIcon />}
                onClick={() => onAction('send', business, offerPayload(trialSlug))}>
                Send onboard email
              </Button>
            )}
            {canManageTrial && (
              <>
                <Button size="small" variant="outlined" onClick={() => onAction('extend', business)}>Extend trial</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => onAction('cancel', business)}>Cancel trial</Button>
              </>
            )}
            {canReactivate && (
              <Button size="small" variant="contained" onClick={() => onAction('reactivate', business)}>Reactivate</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="text" color="error" onClick={() => onAction('delete', business)}>Delete</Button>
            {business.demo_email_sent_at && canSendEmail && (
              <Button size="small" variant="text" startIcon={<EmailOutlinedIcon />}
                onClick={() => onAction('send', business, offerPayload(trialSlug))}>
                Resend
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

// Sqip-admin: pick any org (paginated + searchable) and edit its notification settings.
function OrgNotificationsTab() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [orgs, setOrgs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      const d = await apiCall(ENDPOINTS.ORG_LIST(page, q)).catch(() => ({ results: [], count: 0 }));
      if (!alive) return;
      setOrgs(d.results || []);
      setCount(d.count || 0);
      setLoading(false);
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, page]);

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2 }}>
        <TextField
          placeholder="Search organizations…" size="small" fullWidth value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
        />
        {loading ? <Skeleton height={120} /> : orgs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No organizations found.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {orgs.map((o) => (
              <Box key={o.id} onClick={() => setSelected({ id: o.id, name: o.name })}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1, cursor: 'pointer', borderRadius: 2,
                  bgcolor: selected?.id === o.id ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{[o.city, o.country].filter(Boolean).join(', ')}</Typography>
                </Box>
                {o.status && <Chip size="small" label={o.status} variant="outlined" />}
              </Box>
            ))}
          </Stack>
        )}
        {count > 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Pagination count={Math.ceil(count / 10)} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
          </Box>
        )}
      </Paper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification settings — {selected?.name}</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          {selected && <NotificationsCard orgId={selected.id} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// Billing tab: pick an org and open ITS billing page. Deliberately a link into the existing
// OrgBillingPage rather than a second billing UI — that page already renders subscription,
// payment methods, invoices, usage and notifications, and every endpoint behind it already
// authorizes sqip admins (`_can_view_billing`). A parallel view here would be one more copy
// to keep in sync, which is how fields have gone missing in this codebase before.
export function OrgBillingTab() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [orgs, setOrgs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    const d = await apiCall(ENDPOINTS.ORG_LIST(page, q)).catch(() => ({ results: [], count: 0 }));
    setOrgs(d.results || []);
    setCount(d.count || 0);
    setLoading(false);
  }, [page, q]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => { if (alive) loadOrgs(); }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [loadOrgs]);

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Opens in a new tab so you keep your place here. You&apos;ll see exactly what the owner
        sees — subscription, package, payment methods, invoices and usage — and you can change
        any of it on their behalf.
      </Alert>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <TextField
          placeholder="Search organizations…" size="small" fullWidth value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
        />
        {loading ? <Skeleton height={120} /> : orgs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No organizations found.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {orgs.map((o) => (
              <Box
                key={o.id}
                component="a"
                href={`/admin/billing?org=${o.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                  py: 1, px: 1, cursor: 'pointer', borderRadius: 2,
                  textDecoration: 'none', color: 'inherit',
                  '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[o.city, o.country].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {o.status && <Chip size="small" label={o.status} variant="outlined" />}
                  <Button size="small" variant="outlined" component="span"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Billing
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
        {count > 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Pagination count={Math.ceil(count / 10)} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
          </Box>
        )}
      </Paper>
    </Stack>
  );
}


// Activation tab: sqip admin flips organizations (and their queues) active/inactive without
// deleting anything. Expand an org to reach its categories; deactivating an org cascades.
function OrgActivationTab() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [orgs, setOrgs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);   // org id whose categories are shown
  const [cats, setCats] = useState({});             // orgId -> [categories]
  const [catLoading, setCatLoading] = useState(false);
  const [busy, setBusy] = useState({});             // `${scope}-${id}` -> true while toggling
  const [toast, setToast] = useState('');

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    const d = await apiCall(ENDPOINTS.ORG_LIST(page, q)).catch(() => ({ results: [], count: 0 }));
    setOrgs(d.results || []);
    setCount(d.count || 0);
    setLoading(false);
  }, [page, q]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => { if (alive) loadOrgs(); }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [loadOrgs]);

  const loadCats = async (orgId) => {
    setCatLoading(true);
    const d = await apiCall(ENDPOINTS.CATEGORIES_BY_ORG(orgId)).catch(() => ({ results: [] }));
    setCats((m) => ({ ...m, [orgId]: d.results || d || [] }));
    setCatLoading(false);
  };

  const toggleExpand = (orgId) => {
    if (expanded === orgId) { setExpanded(null); return; }
    setExpanded(orgId);
    if (!cats[orgId]) loadCats(orgId);
  };

  const setOrgStatus = async (org, active) => {
    const key = `org-${org.id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_ORG_STATUS(org.id), {
        method: 'POST',
        body: JSON.stringify({ status: active ? 'active' : 'inactive', cascade: true }),
      });
      setOrgs((list) => list.map((o) => (o.id === org.id ? { ...o, status: res.org_status } : o)));
      if (cats[org.id]) loadCats(org.id); // reflect the cascade on any shown categories
      setToast(`${org.name} ${active ? 'activated' : 'deactivated'}${res.categories_changed ? ` (+${res.categories_changed} queues)` : ''}.`);
    } catch (e) {
      setToast(e.message || 'Failed to update organization.');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const setCatStatus = async (orgId, category, active) => {
    const key = `cat-${category.id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_CATEGORY_STATUS(category.id), {
        method: 'POST',
        body: JSON.stringify({ status: active ? 'active' : 'inactive' }),
      });
      setCats((m) => ({ ...m, [orgId]: (m[orgId] || []).map((c) => (c.id === category.id ? { ...c, status: res.category_status } : c)) }));
      setToast(`${category.name} ${active ? 'activated' : 'deactivated'}.`);
    } catch (e) {
      setToast(e.message || 'Failed to update queue.');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Toggle a business or one of its queues on/off. Deactivating an organization also deactivates
        its queues and hides them from public listings — nothing is deleted.
      </Alert>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2 }}>
        <TextField
          placeholder="Search organizations…" size="small" fullWidth value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
        />
        {loading ? <Skeleton height={160} /> : orgs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No organizations found.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {orgs.map((o) => {
              const isActive = o.status === 'active';
              const orgCats = cats[o.id] || [];
              return (
                <Box key={o.id} sx={{ py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <IconButton size="small" onClick={() => toggleExpand(o.id)}
                      sx={{ transform: expanded === o.id ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{[o.city, o.country].filter(Boolean).join(', ') || '—'}</Typography>
                    </Box>
                    <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'default'} variant={isActive ? 'filled' : 'outlined'} />
                    <Switch checked={isActive} disabled={!!busy[`org-${o.id}`]} onChange={(e) => setOrgStatus(o, e.target.checked)} />
                  </Box>
                  <Collapse in={expanded === o.id} unmountOnExit>
                    <Box sx={{ pl: 5, pb: 1 }}>
                      {catLoading && !cats[o.id] ? <Skeleton height={80} /> : orgCats.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No queues in this organization.</Typography>
                      ) : (
                        <Stack divider={<Divider flexItem />}>
                          {orgCats.map((c) => {
                            const cActive = c.status === 'active';
                            return (
                              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" noWrap>{c.name}</Typography>
                                </Box>
                                <Chip size="small" label={cActive ? 'Active' : 'Inactive'} color={cActive ? 'success' : 'default'} variant={cActive ? 'filled' : 'outlined'} />
                                <Switch size="small" checked={cActive} disabled={!!busy[`cat-${c.id}`]} onChange={(e) => setCatStatus(o.id, c, e.target.checked)} />
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}
        {count > 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Pagination count={Math.ceil(count / 10)} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
          </Box>
        )}
      </Paper>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Stack>
  );
}

function AdminPasswordResetTab() {
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { severity, msg }

  const trigger = async () => {
    const u = username.trim();
    if (!u) { setResult({ severity: 'error', msg: 'Enter a username.' }); return; }
    setBusy(true); setResult(null);
    try {
      const res = await apiCall(ENDPOINTS.ADMIN_PASSWORD_RESET, {
        method: 'POST',
        body: JSON.stringify({ username: u }),
      });
      setResult({ severity: 'success', msg: (res && res.message) || 'Reset link sent.' });
      setUsername('');
    } catch (err) {
      setResult({ severity: 'error', msg: (err && (err.message || (err.body && err.body.message))) || 'Could not send reset link.' });
    }
    setBusy(false);
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Admin password reset</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Send a single-use password-reset link to an admin (org admin, category admin, sqip admin, or
        superuser). Enter their <strong>username</strong> — the link goes to their on-file email and is
        valid for a limited time. Only admin accounts can be reset here.
      </Typography>
      {result && <Alert severity={result.severity} sx={{ mb: 2, borderRadius: 2 }}>{result.msg}</Alert>}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Admin username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && trigger()}
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><VpnKeyOutlinedIcon fontSize="small" color="action" /></InputAdornment>) }}
        />
        <Button variant="contained" onClick={trigger} disabled={busy} startIcon={<SendOutlinedIcon />} sx={{ textTransform: 'none', borderRadius: 2 }}>
          {busy ? 'Sending…' : 'Send reset link'}
        </Button>
      </Box>
    </Box>
  );
}

export default function OnboardingAdminPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  // Free-trial tiers (7/14-day) to grant, and the paid tiers to assign — from the backend.
  const [trialPkgs, setTrialPkgs] = useState([]);
  const [paidPkgs, setPaidPkgs] = useState([]);
  useEffect(() => {
    apiCall(ENDPOINTS.TRIAL_PACKAGES).then((d) => setTrialPkgs((d && d.packages) || [])).catch(() => {});
    apiCall(ENDPOINTS.PACKAGES).then((d) => setPaidPkgs((d && d.packages) || [])).catch(() => {});
  }, []);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(DEFAULT_FILTER);
  const [toast, setToast] = useState('');

  // action dialogs
  const [sendDlg, setSendDlg] = useState({ open: false, biz: null, trial: true, busy: false });
  const [extendDlg, setExtendDlg] = useState({ open: false, biz: null, busy: false });
  const [cancelDlg, setCancelDlg] = useState({ open: false, biz: null, busy: false });
  const [reactivateDlg, setReactivateDlg] = useState({ open: false, biz: null, days: 7, busy: false });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, biz: null, busy: false });
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [ordering, setOrdering] = useState('-registered_at'); // newest first by default
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // gate: sqip admin / staff only
  useEffect(() => {
    const ok = localStorage.getItem('isSqipAdmin') === 'true' || localStorage.getItem('isStaff') === 'true';
    if (!ok) navigate('/', { replace: true });
  }, [navigate]);

  const fetchList = useCallback(async (filterKey, q, pageNum, sort, start, end) => {
    setLoading(true); setError('');
    const filter = FILTERS.find((f) => f.key === filterKey) || FILTERS[0];
    const parts = [`${filter.param}=true`, `page_size=${PAGE_SIZE}`, `page=${pageNum}`, `ordering=${sort}`];
    if (q && q.trim()) parts.push(`search=${encodeURIComponent(q.trim())}`);
    if (start) parts.push(`registered_after=${start}`);
    if (end) parts.push(`registered_before=${end}`);
    try {
      const data = await apiCall(ENDPOINTS.ONBOARDING_LIST(parts.join('&')));
      const results = Array.isArray(data) ? data : data.results || [];
      setBusinesses(results);
      setCount(typeof data.count === 'number' ? data.count : results.length);
    } catch (e) {
      if (e.status === 401 || e.status === 403) { navigate('/', { replace: true }); return; }
      setError('Failed to load businesses.');
    }
    setLoading(false);
  }, [navigate]);

  // refetch on filter/search/sort/date/page (debounced)
  useEffect(() => {
    const id = setTimeout(() => fetchList(activeFilter, search, page, ordering, startDate, endDate), 250);
    return () => clearTimeout(id);
  }, [activeFilter, search, page, ordering, startDate, endDate, fetchList]);

  const refreshAll = () => { fetchList(activeFilter, search, page, ordering, startDate, endDate); };

  const onAction = (type, biz, payload) => {
    if (type === 'send') {
      const defSlug = trialPkgs[0] ? trialPkgs[0].slug : 'trial';
      const slug = payload
        ? (payload.trial_offered ? (payload.trial_package || defSlug) : '')
        : (biz.trial_offered !== false ? (biz.trial_package || defSlug) : '');
      setSendDlg({ open: true, biz, trialSlug: slug, busy: false });
    }
    if (type === 'extend') setExtendDlg({ open: true, biz, busy: false });
    if (type === 'cancel') setCancelDlg({ open: true, biz, busy: false });
    if (type === 'reactivate') setReactivateDlg({ open: true, biz, busy: false });
    if (type === 'delete') setDeleteDlg({ open: true, biz, busy: false });
    if (type === 'saveOffer') submitSaveOffer(biz, payload);
    if (type === 'choosePackage') submitChoosePackage(biz, payload);
  };

  const submitChoosePackage = async (biz, slug) => {
    if (!slug || !biz.organization_id) return;
    try {
      const res = await apiCall(ENDPOINTS.ORG_CHOOSE_PACKAGE(biz.organization_id), {
        method: 'POST', body: JSON.stringify({ package: slug }),
      });
      // Update just this row so the expanded card doesn't collapse (no full refetch).
      setBusinesses((list) => list.map((b) => {
        if (b.id !== biz.id) return b;
        const s = { ...(b.subscription || {}) };
        if (res.scheduled) s.pending_package = (res.pending_package && res.pending_package.slug) || slug;
        else { s.package = (res.package && res.package.slug) || slug; s.pending_package = null; }
        return { ...b, subscription: s };
      }));
      setToast(res.scheduled ? `Package change to ${slug} scheduled.` : `Package set to ${slug}.`);
    } catch (e) {
      setToast((e && (e.message || (e.body && e.body.detail))) || 'Failed to set package.');
    }
  };

  const submitDelete = async () => {
    setDeleteDlg((d) => ({ ...d, busy: true }));
    try {
      await apiCall(ENDPOINTS.ONBOARDING_DELETE(deleteDlg.biz.id), { method: 'POST' });
      setToast(`Deleted ${deleteDlg.biz.business_name}.`);
      setDeleteDlg({ open: false, biz: null, busy: false });
      refreshAll();
    } catch (e) {
      setToast(e.message || 'Failed to delete.');
      setDeleteDlg((d) => ({ ...d, busy: false }));
    }
  };

  const submitSaveOffer = async (biz, patch) => {
    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_APPLY_OFFER(biz.id), {
        method: 'POST', body: JSON.stringify(patch),
      });
      // `activated` => the business had already checked out, so the trial started right away.
      setToast(res.activated
        ? `Free trial started — ${biz.business_name} is now active.`
        : 'Offer saved.');
      // Update just this row so the expanded card doesn't collapse (no full refetch).
      setBusinesses((list) => list.map((b) => (b.id === biz.id ? {
        ...b,
        trial_offered: !!patch.trial_offered,
        trial_package: patch.trial_package || null,
        status: res.onboarding_status || b.status,
      } : b)));
    } catch (e) {
      setToast(e.message || 'Failed to save offer.');
    }
  };

  const submitSend = async () => {
    setSendDlg((d) => ({ ...d, busy: true }));
    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_SEND_EMAIL(sendDlg.biz.id), {
        method: 'POST',
        body: JSON.stringify({ trial: !!sendDlg.trialSlug, trial_package: sendDlg.trialSlug || undefined }),
      });
      setToast(sendDlg.trialSlug ? 'Onboard email sent (free trial).' : 'Onboard email sent (payment required).');
      if (res.checkout_url) {
        try { await navigator.clipboard.writeText(res.checkout_url); setToast('Onboard email sent — checkout link copied.'); } catch { /* ignore */ }
      }
      setSendDlg({ open: false, biz: null, trialSlug: '', busy: false });
      refreshAll();
    } catch (e) {
      setToast(e.message || 'Failed to send email.');
      setSendDlg((d) => ({ ...d, busy: false }));
    }
  };

  const submitExtend = async () => {
    setExtendDlg((d) => ({ ...d, busy: true }));
    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_EXTEND_TRIAL(extendDlg.biz.id), { method: 'POST' });
      setToast('Free trial extended.');
      const bizId = extendDlg.biz.id;
      setExtendDlg({ open: false, biz: null, busy: false });
      // Update just this row so the expanded card stays open (no full refetch). Recompute the
      // "days left" chip client-side from the new trial end date.
      setBusinesses((list) => list.map((b) => {
        if (b.id !== bizId) return b;
        const s = { ...(b.subscription || {}) };
        if (res.trial_ends_at) {
          s.trial_ends_at = res.trial_ends_at;
          s.days_left = Math.max(0, Math.ceil((new Date(res.trial_ends_at) - Date.now()) / 86400000));
        }
        return { ...b, status: res.onboarding_status || b.status, subscription: s };
      }));
    } catch (e) {
      setToast(e.message || 'Failed to extend trial.');
      setExtendDlg((d) => ({ ...d, busy: false }));
    }
  };

  const submitCancel = async () => {
    setCancelDlg((d) => ({ ...d, busy: true }));
    try {
      await apiCall(ENDPOINTS.ONBOARDING_CANCEL_TRIAL(cancelDlg.biz.id), { method: 'POST' });
      setToast('Trial cancelled — account deactivated.');
      setCancelDlg({ open: false, biz: null, busy: false });
      refreshAll();
    } catch (e) {
      setToast(e.message || 'Failed to cancel trial.');
      setCancelDlg((d) => ({ ...d, busy: false }));
    }
  };

  const submitReactivate = async () => {
    setReactivateDlg((d) => ({ ...d, busy: true }));
    try {
      await apiCall(ENDPOINTS.ONBOARDING_REACTIVATE(reactivateDlg.biz.id), { method: 'POST' });
      setToast('Reactivated with a fresh free trial.');
      setReactivateDlg({ open: false, biz: null, busy: false });
      refreshAll();
    } catch (e) {
      setToast(e.message || 'Failed to reactivate.');
      setReactivateDlg((d) => ({ ...d, busy: false }));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 4, sm: 6 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Fade in timeout={400}>
          <Box>
            <Chip label="Internal · Sqip admin" size="small" variant="outlined" sx={{ mb: 2, fontWeight: 600, fontSize: 12 }} />
            <Typography variant="h4" fontWeight={800}>On-boarding Panel</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Track and manage businesses through the on-boarding funnel.
            </Typography>
          </Box>
        </Fade>

        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab icon={<BusinessOutlinedIcon fontSize="small" />} iconPosition="start" label="Funnel" sx={{ minHeight: 56 }} />
            <Tab icon={<InsightsOutlinedIcon fontSize="small" />} iconPosition="start" label="Insights" sx={{ minHeight: 56 }} />
            <Tab icon={<NotificationsNoneOutlinedIcon fontSize="small" />} iconPosition="start" label="Notifications" sx={{ minHeight: 56 }} />
            <Tab icon={<ToggleOnOutlinedIcon fontSize="small" />} iconPosition="start" label="Activation" sx={{ minHeight: 56 }} />
            <Tab icon={<ReceiptLongOutlinedIcon fontSize="small" />} iconPosition="start" label="Billing" sx={{ minHeight: 56 }} />
            <Tab icon={<VpnKeyOutlinedIcon fontSize="small" />} iconPosition="start" label="Admin access" sx={{ minHeight: 56 }} />
          </Tabs>
        </Paper>

        {tab === 0 && (
        <>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' } }}>
          <TextField
            placeholder="Search name, email, reference…" size="small" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} fullWidth
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' } }}
          />
          <FormControl size="small" sx={{ minWidth: 230, flexShrink: 0 }}>
            <InputLabel id="oba-filter-label">Funnel stage</InputLabel>
            <Select
              labelId="oba-filter-label" label="Funnel stage" value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
              renderValue={(sel) => FILTERS.find((f) => f.key === sel)?.label || ''}
              startAdornment={<FilterListIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />}
              sx={{ borderRadius: 2.5, bgcolor: 'background.paper' }}
            >
              {FILTERS.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  <Checkbox checked={activeFilter === f.key} size="small" sx={{ p: 0, mr: 1 }} />
                  <ListItemText primary={f.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh list">
            <span>
              <IconButton onClick={refreshAll} disabled={loading} aria-label="Refresh list"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-label">Sort</InputLabel>
            <Select labelId="sort-label" label="Sort" value={ordering} onChange={(e) => { setOrdering(e.target.value); setPage(1); }} sx={{ borderRadius: 2.5, bgcolor: 'background.paper' }}>
              <MenuItem value="-registered_at">Newest first</MenuItem>
              <MenuItem value="registered_at">Oldest first</MenuItem>
              <MenuItem value="business_name">Name A–Z</MenuItem>
              <MenuItem value="-business_name">Name Z–A</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' } }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' } }} />
          {(startDate || endDate) && (
            <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}>Clear dates</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">{count} total</Typography>
        </Box>

        <Box>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          {loading ? (
            <Stack spacing={1.25}>
              {[...Array(4)].map((_, i) => (
                <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}><Skeleton width="40%" height={22} /><Skeleton width="60%" height={16} /></Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : businesses.length === 0 ? (
            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
              <BusinessOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {search ? `No businesses matching "${search}" in this stage.` : 'No businesses in this stage yet.'}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {businesses.map((b) => <BusinessRow key={b.id} business={b} onAction={onAction} trialPkgs={trialPkgs} paidPkgs={paidPkgs} />)}
            </Stack>
          )}
        </Box>

        {!loading && count > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
        </>
        )}

        {tab === 1 && <InsightsPanel />}

        {tab === 2 && <OrgNotificationsTab />}

        {tab === 3 && <OrgActivationTab />}

        {tab === 4 && <OrgBillingTab />}

        {tab === 5 && <AdminPasswordResetTab />}
      </Box>

      {/* Send onboard email dialog */}
      <Dialog open={sendDlg.open} onClose={() => !sendDlg.busy && setSendDlg((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Send onboard email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Emails {sendDlg.biz?.business_name} a checkout link and moves them to Post-Demo.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="send-offer" shrink>Offer</InputLabel>
            <Select labelId="send-offer" label="Offer" value={sendDlg.trialSlug || ''} displayEmpty notched
              onChange={(e) => setSendDlg((d) => ({ ...d, trialSlug: e.target.value }))}>
              <MenuItem value="">No free trial (payment required)</MenuItem>
              {trialPkgs.map((p) => <MenuItem key={p.slug} value={p.slug}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDlg((d) => ({ ...d, open: false }))} disabled={sendDlg.busy}>Cancel</Button>
          <Button variant="contained" onClick={submitSend} disabled={sendDlg.busy}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* Extend trial dialog */}
      <Dialog open={extendDlg.open} onClose={() => !extendDlg.busy && setExtendDlg((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Extend trial</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Extends {extendDlg.biz?.business_name}'s free trial by another full trial period from the Free Trial package.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDlg((d) => ({ ...d, open: false }))} disabled={extendDlg.busy}>Cancel</Button>
          <Button variant="contained" onClick={submitExtend} disabled={extendDlg.busy}>Extend</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel trial confirm */}
      <Dialog open={cancelDlg.open} onClose={() => !cancelDlg.busy && setCancelDlg((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel trial?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This deactivates {cancelDlg.biz?.business_name} and turns their queue off.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDlg((d) => ({ ...d, open: false }))} disabled={cancelDlg.busy}>Keep trial</Button>
          <Button variant="contained" color="error" onClick={submitCancel} disabled={cancelDlg.busy}>Cancel trial</Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate dialog */}
      <Dialog open={reactivateDlg.open} onClose={() => !reactivateDlg.busy && setReactivateDlg((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Reactivate business</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Re-enables {reactivateDlg.biz?.business_name} with a fresh free trial and turns their queue back on.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateDlg((d) => ({ ...d, open: false }))} disabled={reactivateDlg.busy}>Cancel</Button>
          <Button variant="contained" onClick={submitReactivate} disabled={reactivateDlg.busy}>Reactivate</Button>
        </DialogActions>
      </Dialog>

      {/* Delete business confirm */}
      <Dialog open={deleteDlg.open} onClose={() => !deleteDlg.busy && setDeleteDlg((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete business?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Permanently deletes <b>{deleteDlg.biz?.business_name}</b> and everything linked — its
            organization, categories, appointments, subscription and payment history. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg((d) => ({ ...d, open: false }))} disabled={deleteDlg.busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitDelete} disabled={deleteDlg.busy}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
